/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useServiceContext } from '../../root'
import { useModal } from '../../contexts/ModalContext'
import { useEsignature } from '../../hooks/useEsignature'
import Modal from '../Modal'
import LoadingSpinner from '../LoadingSpinner'
import ActionsMenu from '../shared/ActionsMenu'
import TextLinkButton from '../shared/TextLinkButton'
import { WorkflowDetailsModal } from './WorkflowDetailsModal'
import ShareLinkModal from '../../pages/browser/ShareLinkModal'
import { RemoveDraftsModal } from './RemoveDraftsModal'
import { SmallBoxModal } from './SmallBoxModal'
import {
  getFile,
  getFileVersions,
  downloadFsiVersionPdf,
  revertFileToVersion,
  type FileVersions
} from '../../services/api/files'
import { File } from '../../constants/file'
import { SystemFolderType } from '../../constants/fsi'
import { UserRole } from '../../types/access'

interface VersionsModalProps {
  fsiId: string
  onClose?: () => void
}

interface Version {
  versionId: number
  minorVersionId: number
  fileId?: number
  fsiId?: number
  [key: string]: any
}

interface VersionGroup {
  version: Version
  relatedVersions?: Version[]
}

export const VersionsModal: React.FC<VersionsModalProps> = ({ fsiId, onClose }) => {
  const context = useServiceContext()
  const {
    service: {
      filter: { translate, date },
      auth
    }
  } = context
  const { hideModal, showModal } = useModal()
  const { signAction } = useEsignature()
  const navigate = useNavigate()
  const [documentName, setDocumentName] = useState('')
  const [file, setFile] = useState<any>(null)
  const [changeRequestVersions, setChangeRequestVersions] = useState(false)
  const [groupedVersions, setGroupedVersions] = useState<VersionGroup[]>([])
  const [versions, setVersions] = useState<Version[]>([])
  const [activeRows, setActiveRows] = useState<boolean[]>([])
  const [loading, setLoading] = useState(true)

  // Helper functions
  const bytes = (bytes: number): string => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    if (bytes === 0) return '0 Bytes'
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i]
  }

  const excerpt = (text: string, stripHtml: boolean = false, length: number = 100): string => {
    const cleanText = stripHtml ? text.replace(/<[^>]*>/g, '') : text
    return cleanText.length > length ? cleanText.substring(0, length) + '...' : cleanText
  }

  // downloadFsiVersionPdf is now imported from ../../services/api/files

  useEffect(() => {
    // Add modal-open class to body
    document.body.classList.add('modal-open')

    const loadVersions = async () => {
      try {
        setLoading(true)

        // Call 1: Get file details - /api/files/{fsiId}
        const fileData = await getFile(Number(fsiId))
        setFile(fileData)
        setDocumentName(fileData.name || '')
        // Check if this is a workflow-related file by checking systemFolderType
        setChangeRequestVersions((fileData as any)?.systemFolderType === SystemFolderType.WORKFLOWS)

        // Call 2: Get file versions - /api/files/{fsiId}/versions
        const versionsData: FileVersions = await getFileVersions(Number(fsiId))

        // Add fileId to each version (matching legacy behavior)
        const enhancedVersions =
          versionsData.versions?.map((obj: any) => ({
            ...obj,
            fileId: obj?.fsiId || obj?.id
          })) || []

        // Filter versions based on user role (matching legacy logic)
        const userRole = auth.getRole()
        const isAuditorOrInspector = userRole && ['AUDITOR', 'INSPECTOR'].includes(userRole)

        let filteredVersions: Version[] = enhancedVersions
        if (isAuditorOrInspector) {
          filteredVersions = enhancedVersions.filter((item: any) => {
            return !item?.minorVersionId || item?.versionState !== File.lifecycleState.DRAFT
          })
        }

        // Store all versions for comparison menu
        setVersions([...filteredVersions])

        // Group versions (major and minor)
        const grouped = groupVersions(filteredVersions)
        setGroupedVersions(grouped)

        setLoading(false)
      } catch (error) {
        console.error('Error loading versions:', error)
        setLoading(false)
      }
    }

    loadVersions()

    return () => {
      // Clean up: remove modal-open class when modal unmounts
      document.body.classList.remove('modal-open')
    }
  }, [fsiId, auth])

  const groupVersions = (versions: Version[]): VersionGroup[] => {
    const groupedVersions: VersionGroup[] = []
    let minorVersions: Version[] = []
    const versionsCopy = [...versions]

    const getVersionInfo = (version: Version, relatedVersions?: Version[]): VersionGroup => {
      return {
        version,
        relatedVersions
      }
    }

    // Process versions in reverse order
    versionsCopy.reverse()
    versionsCopy.forEach((version) => {
      if (version.minorVersionId) {
        minorVersions.push(version)
      } else {
        // Reverse minor versions for easier UI listing
        groupedVersions.push(getVersionInfo(version, minorVersions.toReversed()))
        minorVersions = []
      }
    })

    // Handle any remaining minor versions
    if (minorVersions.length) {
      minorVersions.forEach((version) => {
        groupedVersions.push(getVersionInfo(version))
      })
    }

    // Get the latest version on top (as that's what UI requires)
    return groupedVersions.reverse()
  }

  const toggleRow = (index: number) => {
    const newActiveRows = [...activeRows]
    newActiveRows[index] = !newActiveRows[index]
    setActiveRows(newActiveRows)
  }

  const rowActive = (index: number) => {
    return activeRows[index]
  }

  const compareAgainst = (versionToCompare: Version, withCompare: Version, isDetailed: boolean) => {
    if (!withCompare) return

    handleClose()
    setTimeout(() => {
      const searchParams = new URLSearchParams(window.location.search)
      searchParams.set('file2', String(withCompare.fileId))
      searchParams.set('version', String(versionToCompare.versionId))
      searchParams.set('versionCompareId', String(withCompare.versionId))
      searchParams.set('minorVersionId', String(versionToCompare.minorVersionId))
      searchParams.set('minorVersionId2', String(withCompare.minorVersionId))
      searchParams.set('detailed', String(isDetailed))
      navigate(`/show-file-diff/${String(versionToCompare.fileId)}?${searchParams.toString()}`)
    }, 100)
  }

  const revert = async (versionId: number) => {
    const doRevert = async (validation?: any) => {
      try {
        await revertFileToVersion(Number(fsiId), versionId, validation)
        context.service.toast.success(translate('BROWSER_VERSION_FILE_WAS_REVERTED_MSG'))
        // Trigger file update event
        window.dispatchEvent(new CustomEvent('updateFiles'))
        // Close modal after successful revert
        handleClose()
      } catch (error: any) {
        context.service.toast.error(translate('ERROR_ERROR', { error: error?.body?.message || error?.message }))
      }
    }

    if (auth.requireESignatureForFileCommands()) {
      try {
        // Use signAction from useEsignature hook (matches legacy Versions.jsx implementation)
        const validation = await signAction('ADMIN_ACTION', 'ADMIN_ACTION_DOCUMENT_REVERTED_TO_VERSION', {})
        await doRevert(validation)
      } catch (error) {
        // User cancelled e-signature or it failed
        console.log('E-signature cancelled or failed')
      }
    } else {
      await doRevert()
    }
  }

  const isDownloadable = () => {
    return auth.getRole() !== UserRole.INSPECTOR
  }

  const isComparable = (item: Version) => {
    if (!file) return false

    const extension = file.name?.split('.').pop()?.toLowerCase() || ''
    const comparableExtensions = ['docx', 'doc', 'pdf']

    return (
      comparableExtensions.includes(extension) &&
      !(auth.getRole() === UserRole.INSPECTOR && item.versionState === File.lifecycleState.RETIRED)
    )
  }

  const showRemoveDrafts = (versionId: number) => {
    // Open RemoveDraftsModal with file ID and version ID
    showModal(
      <RemoveDraftsModal
        fsiId={Number(fsiId)}
        versionId={versionId}
        onClose={() => hideModal()}
        onSuccess={() => {
          // Reload versions after successful removal
          window.location.reload()
        }}
      />,
      { keepParentModalsOpen: true }
    )
  }

  const showCRDesc = (text: string, titleKey: string) => {
    // Show modal with full text using SmallBoxModal component
    showModal(<SmallBoxModal text={text} titleKey={titleKey} onClose={() => {}} />, {
      keepParentModalsOpen: true
    })
  }

  const configureAndSetMenuOptions = (item: VersionGroup) => {
    const menu: any[] = []

    // View action - different logic for EFFECTIVE vs non-EFFECTIVE
    if (item?.version?.versionState === File.lifecycleState.EFFECTIVE && !changeRequestVersions) {
      menu.push({
        value: 'BROWSER_ACTION_SHOW_FILE',
        action: () => {
          handleClose()
          // Use same params structure as legacy: file, version, minorVersion, taskId, action
          const searchParams = new URLSearchParams(window.location.search)
          searchParams.set('version', '-1')
          searchParams.set('minorVersion', '0')
          searchParams.set('taskId', '0')
          searchParams.set('action', 'none')
          navigate(`/show-file/${String(item?.version?.fileId)}?${searchParams.toString()}`)
        }
      })
    }

    if (item?.version?.versionState !== File.lifecycleState.EFFECTIVE || changeRequestVersions) {
      menu.push({
        value: 'BROWSER_ACTION_SHOW_FILE',
        action: () => {
          handleClose()
          const searchParams = new URLSearchParams(window.location.search)
          searchParams.set('version', String(item?.version?.versionId))
          searchParams.set('minorVersion', String(item?.version?.minorVersionId))
          searchParams.set('taskId', '0')
          searchParams.set('action', 'none')
          navigate(`/show-file/${String(item?.version?.fileId)}?${searchParams.toString()}`)
        }
      })
    }

    // Share Link action
    if (item?.version?.canShareLink) {
      menu.push({
        value: 'BROWSER_ACTION_SHARE_FILE_LINK',
        action: () => {
          showModal(
            <ShareLinkModal
              fileDetails={{ id: item?.version?.fileId, name: file?.name }}
              versionInfo={{ versionId: item?.version?.versionId, minorVersionId: item?.version?.minorVersionId }}
            />
          )
        }
      })
    }

    // Download action
    if (isDownloadable()) {
      menu.push({
        value: 'DOWNLOAD',
        action: () => {
          if (item?.version?.fileId && item?.version?.versionId !== undefined) {
            downloadFsiVersionPdf(item.version.fileId, item.version.versionId, item.version.minorVersionId || 0)
          }
        }
      })
    }

    // Revert To action - matching legacy logic from Versions.jsx
    // Legacy uses access.isAllowed([accessTable.contentAdmins, "POWER_USER"])
    // where accessTable.contentAdmins = ['ADMIN', 'CONTENT_ADMIN', 'ETMF_MANAGER', 'ETMF_SPECIALIST']
    if (
      item?.version?.versionState !== File.lifecycleState.EFFECTIVE &&
      !(file as any)?.inWorkflow &&
      !(file as any)?.workflowDirectory &&
      context.service.access.isAllowed([
        UserRole.ADMIN,
        UserRole.CONTENT_ADMIN,
        UserRole.ETMF_MANAGER,
        UserRole.ETMF_SPECIALIST,
        UserRole.POWER_USER
      ]) &&
      item?.version?.minorVersionId === 0
    ) {
      menu.push({
        value: 'BROWSER_VERSIONS_ACTION_REVERT_TO',
        action: () => {
          revert(item?.version?.versionId)
        }
      })
    }

    // Compare With submenu
    const subMenu: any[] = []
    versions.forEach((verToChoose) => {
      if (
        item?.version?.versionId !== verToChoose?.versionId ||
        item?.version?.minorVersionId !== verToChoose?.minorVersionId
      ) {
        subMenu.push({
          value: verToChoose.versionId + '.' + verToChoose.minorVersionId,
          subSubmenu: [
            {
              value: 'FILE_DIFF_SHOW_DETAILED',
              action: () => {
                const version = item?.version
                if (version) compareAgainst(version, verToChoose, true)
              }
            },
            {
              value: 'FILE_DIFF_SHOW_OVERVIEW',
              action: () => {
                const version = item?.version
                if (version) compareAgainst(version, verToChoose, false)
              }
            }
          ]
        })
      }
    })

    const version = item?.version
    if (version && isComparable(version) && versions?.length > 1) {
      menu.push({
        value: 'BROWSER_VERSIONS_ACTION_COMPARE_WITH',
        submenu: subMenu
      })
    }

    // Remove Draft Versions action
    // Legacy uses access.isAllowed([accessTable.contentAdmins, "POWER_USER"])
    // where accessTable.contentAdmins = ['ADMIN', 'CONTENT_ADMIN', 'ETMF_MANAGER', 'ETMF_SPECIALIST']
    if (
      context.service.access.isAllowed([
        UserRole.ADMIN,
        UserRole.CONTENT_ADMIN,
        UserRole.ETMF_MANAGER,
        UserRole.ETMF_SPECIALIST,
        UserRole.POWER_USER
      ]) &&
      item?.relatedVersions &&
      item.relatedVersions.length > 0
    ) {
      menu.push({
        value: 'REMOVE_DRAFT_VERSIONS',
        action: () => {
          showRemoveDrafts(item?.version?.versionId)
        }
      })
    }

    return menu
  }

  const configureAndSetMinorVersionMenuOptions = (version: Version) => {
    const menu: any[] = []

    // Show File action (minor versions do NOT close modal)
    menu.push({
      value: 'BROWSER_ACTION_SHOW_FILE',
      action: () => {
        const searchParams = new URLSearchParams(window.location.search)
        searchParams.set('version', String(version?.versionId))
        searchParams.set('minorVersion', String(version?.minorVersionId))
        searchParams.set('taskId', '0')
        searchParams.set('action', 'none')
        navigate(`/show-file/${String(version?.fileId)}?${searchParams.toString()}`)
      }
    })

    // Share Link action
    if (version?.canShareLink) {
      menu.push({
        value: 'BROWSER_ACTION_SHARE_FILE_LINK',
        action: () => {
          showModal(
            <ShareLinkModal
              fileDetails={{ id: version?.fileId, name: file?.name }}
              versionInfo={{ versionId: version?.versionId, minorVersionId: version?.minorVersionId }}
            />
          )
        }
      })
    }

    // Download action
    if (isDownloadable()) {
      menu.push({
        value: 'DOWNLOAD',
        action: () => {
          if (version?.fileId && version?.versionId !== undefined) {
            downloadFsiVersionPdf(version.fileId, version.versionId, version.minorVersionId || 0)
          }
        }
      })
    }

    // Compare With submenu
    const subMenu: any[] = []
    versions.forEach((verToChoose) => {
      if (version?.versionId !== verToChoose?.versionId || version?.minorVersionId !== verToChoose?.minorVersionId) {
        subMenu.push({
          value: verToChoose.versionId + '.' + verToChoose.minorVersionId,
          subSubmenu: [
            {
              value: 'FILE_DIFF_SHOW_DETAILED',
              action: () => compareAgainst(version, verToChoose, true)
            },
            {
              value: 'FILE_DIFF_SHOW_OVERVIEW',
              action: () => compareAgainst(version, verToChoose, false)
            }
          ]
        })
      }
    })

    if (isComparable(version) && versions?.length > 1) {
      menu.push({
        value: 'BROWSER_VERSIONS_ACTION_COMPARE_WITH',
        submenu: subMenu
      })
    }

    return menu
  }

  const handleClose = () => {
    if (onClose) {
      onClose()
    }
    hideModal()
  }

  return (
    <Modal show={true} handleClose={handleClose} size="lg" title={translate('BROWSER_VERSIONS_H_TITLE')}>
      <div className="versions-dialog">
        <Modal.Body>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <LoadingSpinner size={40} color="#3374c8" />
            </div>
          ) : (
            <div className="theme-sandman">
              <div className="form-group wparag">
                <label className="control-label">{translate('DOCUMENT')}</label>
                <p className="whitespaceprewrap">{documentName}</p>
              </div>

              <div className="form-group wparag">
                <label className="control-label">{translate('CHANGE_HISTORY')}</label>
              </div>

              <div className="grouped-versions-table custom-version-table custom-actions-button">
                <div className="table-header">
                  <div className="h versions-col-version">{translate('VERSION')}</div>
                  <div className="h versions-col-effective-date">{translate('EFFECTIVE_DATE')}</div>
                  <div className="h size versions-col-size">{translate('SIZE')}</div>
                  <div className="h versions-col-cr-number">{translate('CR_#')}</div>
                  <div className="h versions-col-initiator">{translate('INITIATOR')}</div>
                  <div className="h versions-col-description">{translate('DESCRIPTION')}</div>
                  <div className="h actions versions-col-actions">{translate('ACTIONS')}</div>
                </div>

                {groupedVersions.map((item, index) => (
                  <div
                    key={index}
                    className={`main-row ${(item?.relatedVersions?.length ?? 0) > 0 ? 'expandable' : ''} ${item?.version?.minorVersionId === 0 ? 'major-version' : ''} ${rowActive(index) ? 'active' : ''}`}
                  >
                    <div className="table-row" style={{ fontSize: '12px' }}>
                      <div className="h version ellipsis versions-col-version">
                        {(item?.relatedVersions?.length ?? 0) > 0 && (
                          <a onClick={() => toggleRow(index)}>
                            {`${item?.version?.versionId}.${item?.version?.minorVersionId}`}
                          </a>
                        )}
                        {!(item?.relatedVersions?.length ?? 0) && (
                          <span>
                            {`${item?.version?.versionId}.${item?.version?.minorVersionId}`}
                            {item?.version?.versionState === 'REJECTED' && (
                              <span> ({translate('WORKFLOWS_STEP_STATUS_REJECTED')})</span>
                            )}
                          </span>
                        )}
                      </div>
                      <div className="h versions-col-effective-date">
                        {item?.version?.effectiveDate
                          ? date(item.version.effectiveDate, false)
                          : translate('NOT_AVAILABLE')}
                      </div>
                      <div className="h size versions-col-size">{bytes(item?.version?.size)}</div>
                      <div className="h versions-col-cr-number">
                        {item?.version?.crNumber && (
                          <TextLinkButton
                            action={() => {
                              showModal(<WorkflowDetailsModal wfId={Number(item.version.crNumber)} />)
                            }}
                          >
                            {item?.version?.crFormattedNumber}
                          </TextLinkButton>
                        )}
                        {item?.version?.crFormattedNumber ? '' : translate('NOT_AVAILABLE')}
                      </div>
                      <div className="h versions-col-initiator">
                        {item?.version?.crInitiatorName?.length > 15 && (
                          <span
                            className="cursor-pointer hover-underline"
                            onClick={() => showCRDesc(item.version.crInitiatorName, 'INITIATOR')}
                            title={item.version.crInitiatorName}
                          >
                            {excerpt(item.version.crInitiatorName, false, 15)}
                          </span>
                        )}
                        {item?.version?.crInitiatorName?.length <= 15 && (
                          <span>
                            {item.version.crInitiatorName ? item.version.crInitiatorName : translate('NOT_AVAILABLE')}
                          </span>
                        )}
                      </div>
                      <div className="h versions-col-description">
                        {item?.version?.crDescription?.length > 29 && (
                          <span
                            className="cursor-pointer hover-underline"
                            onClick={() => showCRDesc(item.version.crDescription, 'DESCRIPTION')}
                            title={item.version.crDescription}
                          >
                            {excerpt(item.version.crDescription, false, 30)}
                          </span>
                        )}
                        {item?.version?.crDescription?.length <= 29 && (
                          <span>
                            {item.version.crDescription ? item.version.crDescription : translate('NOT_AVAILABLE')}
                          </span>
                        )}
                      </div>
                      <div className="h actions versions-col-actions custom-actions-button-wrapper">
                        <ActionsMenu menuId={`version-menu-${index}`} items={configureAndSetMenuOptions(item)} />
                      </div>
                    </div>

                    {item?.relatedVersions && (
                      <div className="minor-versions">
                        {item.relatedVersions.map((key, subIndex) => (
                          <div key={subIndex} className="table-row">
                            <div className="h version versions-col-version">
                              {`${key?.versionId}.${key?.minorVersionId}`}
                              {key?.versionState === 'REJECTED' && (
                                <span> ({translate('WORKFLOWS_STEP_STATUS_REJECTED')})</span>
                              )}
                            </div>
                            <div className="h versions-col-effective-date">
                              {key?.effectiveDate ? date(key.effectiveDate, false) : translate('NOT_AVAILABLE')}
                            </div>
                            <div className="h size versions-col-size">{bytes(key?.size)}</div>
                            <div className="h versions-col-cr-number">
                              {key?.crNumber && (
                                <TextLinkButton
                                  action={() => {
                                    showModal(<WorkflowDetailsModal wfId={Number(key.crNumber)} />)
                                  }}
                                >
                                  {key?.crFormattedNumber}
                                </TextLinkButton>
                              )}
                              {key?.crFormattedNumber ? '' : translate('NOT_AVAILABLE')}
                            </div>
                            <div className="h versions-col-initiator">
                              {key?.crInitiatorName?.length > 15 && (
                                <span
                                  className="cursor-pointer hover-underline"
                                  onClick={() => showCRDesc(key.crInitiatorName, 'INITIATOR')}
                                  title={key?.crInitiatorName}
                                >
                                  {excerpt(key?.crInitiatorName, false, 15)}
                                </span>
                              )}
                              {key?.crInitiatorName?.length <= 15 && (
                                <span>{key?.crInitiatorName ? key.crInitiatorName : translate('NOT_AVAILABLE')}</span>
                              )}
                            </div>
                            <div className="h versions-col-description">
                              {key?.crDescription?.length > 29 && (
                                <span
                                  className="cursor-pointer hover-underline"
                                  onClick={() => showCRDesc(key.crDescription, 'DESCRIPTION')}
                                  title={key?.crDescription}
                                >
                                  {excerpt(key.crDescription, false, 30)}
                                </span>
                              )}
                              {key?.crDescription?.length <= 29 && (
                                <span>{key?.crDescription ? key.crDescription : translate('NOT_AVAILABLE')}</span>
                              )}
                            </div>
                            <div className="h actions versions-col-actions">
                              <ActionsMenu
                                menuId={`minor-version-menu-${index}-${subIndex}`}
                                items={configureAndSetMinorVersionMenuOptions(key)}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button data-action="save" className="btn btn-primary" onClick={handleClose} data-test-id="save">
            {translate('OK')}
          </button>
        </Modal.Footer>
      </div>
    </Modal>
  )
}

export default VersionsModal
